import React, {
  ReactElement, useCallback, useEffect, useState
} from 'react';
import Script from 'next/script';
import { useDispatch } from 'react-redux';
import getConfig from 'next/config';
import { openModal } from '@/localization/actions';
import { errorModalConfig } from '../common/ChatLiveAgentConfig';
import ContactLiveAgentButton from '../ContactBtn/ContactLiveAgentButton';
import {
  SEND_BTN_CHAT_WRAPPER,
  NOTIFICATION_CHAT_WRAPPER,
  PRECHAT_DEFAULT_UI_CLASS,
  PRECHAT_WELCOME_WRAPPER,
  SALESFORCE_CHAT_SCRIPT,
  UPDATE_AGENT_AVAIL_LISTENER,
  initializeChatSDK
} from './utils';

interface ChatInitCallCompletedHandlerProps {
  isAgentAvailable: boolean;
}

interface AgentAvailabilityHandlerProps {
  id: string;
  isAgentAvailable: boolean;
}

interface UpdateAgentAvailabilityHandlerProps extends CustomEvent {
  detail: boolean;
}

const LiveAgentWrapper = (): ReactElement => {
  const dispatch = useDispatch();
  const { publicRuntimeConfig } = getConfig();
  const [isAgentAvailable, setIsAgentAvailable] = useState(true);

  const removeNotification = () => {
    const notificationWrapper = document.querySelector(`.${NOTIFICATION_CHAT_WRAPPER}`);
    notificationWrapper?.remove();
  };

  const insertWelcomeIntro = () => {
    const divWelcomeWrapper = document.createElement('div');
    divWelcomeWrapper.className = PRECHAT_WELCOME_WRAPPER;
    divWelcomeWrapper.innerHTML = '<h3 class="preChatHeading">Welcome!</h3><p>We need a little more info before we can assign a chat agent:</p>';

    const preChatWrapper = document.querySelector(PRECHAT_DEFAULT_UI_CLASS);

    preChatWrapper?.prepend(divWelcomeWrapper);
  };

  const insertContactOrderFieldHelper = () => {
    const contactOrderOptionalWrapper = document.createElement('span');
    contactOrderOptionalWrapper.innerHTML = ' (Optional)';

    const contactOrderLabelWrapper = document.getElementById('Are_you_contacting_us_about_an_order__c-label');
    contactOrderLabelWrapper?.append(contactOrderOptionalWrapper);
  };

  const insertOrderDateFieldHelper = () => {
    const orderHelperWrapper = document.createElement('p');
    orderHelperWrapper.className = 'fieldHelperText';
    orderHelperWrapper.innerHTML = 'MM/DD/YYYY';

    const orderOptionalWrapper = document.createElement('span');
    orderOptionalWrapper.innerHTML = ' (Optional)';

    const orderLabelWrapper = document.getElementById('Order_Date__c-label');
    orderLabelWrapper?.append(orderOptionalWrapper);

    const orderDateWrapper = document.getElementById('Order_Date__c');
    orderDateWrapper?.insertAdjacentElement('afterend', orderHelperWrapper);
  };

  const insertFieldFocus = () => {
    const inputFields = document.querySelectorAll('.slds-style-inputtext.input');
    const inputFieldsLength = inputFields.length;
    const selectFields = document.getElementById('Are_you_contacting_us_about_an_order__c');

    for (let loop = 0; loop < inputFieldsLength; loop += 1) {
      const focusDivWrapper = document.createElement('div');
      focusDivWrapper.className = 'focusField';

      inputFields[loop]?.insertAdjacentElement('afterend', focusDivWrapper);
    }

    const selectFocusDivWrapper = document.createElement('div');
    selectFocusDivWrapper.className = 'focusField';

    selectFields?.insertAdjacentElement('afterend', selectFocusDivWrapper);
  };

  const insertPreChatIntro = () => {
    if (document.querySelector(PRECHAT_DEFAULT_UI_CLASS) && !document.querySelector(`.${PRECHAT_WELCOME_WRAPPER}`)) {
      insertWelcomeIntro();
      insertContactOrderFieldHelper();
      insertOrderDateFieldHelper();
      insertFieldFocus();
    }
  };

  const agentAvailabilityHandler = (data: AgentAvailabilityHandlerProps) => {
    if (document.getElementById('liveAgentButton')) {
      const updateAgentAvailEvent = new CustomEvent(UPDATE_AGENT_AVAIL_LISTENER, {
        detail: !!data?.isAgentAvailable
      });
      document.dispatchEvent(updateAgentAvailEvent);
    }
  };

  const chatInitCallCompletedHandler = (data: ChatInitCallCompletedHandlerProps) => {
    const updateAgentAvailEvent = new CustomEvent(UPDATE_AGENT_AVAIL_LISTENER, {
      detail: !!data?.isAgentAvailable
    });
    document.dispatchEvent(updateAgentAvailEvent);
  };

  const newAgentMessageHandler = () => {
    const modalWrapper = document.querySelector('.modalContainer.sidebarMinimized');

    if (!modalWrapper) {
      return;
    }

    let notificationWrapper = document.querySelector(`.${NOTIFICATION_CHAT_WRAPPER}`);

    if (!notificationWrapper) {
      notificationWrapper = document.createElement('div');
      notificationWrapper.className = NOTIFICATION_CHAT_WRAPPER;
      notificationWrapper.innerHTML = '1';
    } else {
      let total: number | string = Number(notificationWrapper.innerHTML) + 1;

      if (total > 100 || notificationWrapper.innerHTML === '100+') {
        total = '100+';
      }

      notificationWrapper.innerHTML = String(total);
    }

    const helpButtonWrapper = document.querySelector('.minimizedContainer.embeddedServiceSidebarMinimizedDefaultUI');

    helpButtonWrapper?.prepend(notificationWrapper);
  };

  const afterMaximizeHandler = () => {
    insertPreChatIntro();
    removeNotification();
  };

  const chatSendMessageHandler = () => {
    const keyDownEvent = new KeyboardEvent('keydown', {
      key: 'Enter',
      keyCode: 13
    });

    document.getElementById('120:0')?.dispatchEvent(keyDownEvent);
  };

  const chatEstablishedHandler = () => {
    // To ensure the code below is added correctly a setTimeout is used to give the DOM the time needed to add the chat message page state.
    setTimeout(() => {
      let sendButtonWrapper = document.querySelector(`.${SEND_BTN_CHAT_WRAPPER}`);

      if (!sendButtonWrapper) {
        sendButtonWrapper = document.createElement('button');
        sendButtonWrapper.className = SEND_BTN_CHAT_WRAPPER;
        sendButtonWrapper.innerHTML = 'Send';

        const addWrapper = document.querySelector('.chasitorText.uiInput--textarea');

        addWrapper?.insertAdjacentElement('afterend', sendButtonWrapper);

        document.querySelector(`.${SEND_BTN_CHAT_WRAPPER}`)?.addEventListener('click', chatSendMessageHandler);
      }
    }, 0);
  };

  const initLiveAgent = (gslbBaseURL: string | null) => {
    initializeChatSDK(publicRuntimeConfig, gslbBaseURL, {
      agentAvailabilityHandler,
      chatInitCallCompletedHandler,
      newAgentMessageHandler,
      chatEstablishedHandler,
      afterMaximizeHandler
    });
  };

  const onLiveAgentError = useCallback(() => {
    const modalContent: ModalContent = errorModalConfig;
    dispatch(openModal(modalContent));
  }, [dispatch]);

  const onSalesforceServiceLoaded = () => {
    if (!window.embedded_svc && document.querySelector(`.${SALESFORCE_CHAT_SCRIPT}`)) {
      return;
    }

    if (!window.embedded_svc) {
      const scriptObj = document.createElement('script');
      scriptObj.setAttribute('src', publicRuntimeConfig.SALESFORCE_PH_EMBEDDED_URL);
      scriptObj.setAttribute('class', SALESFORCE_CHAT_SCRIPT);
      scriptObj.onload = () => {
        initLiveAgent(null);
      };
      document.body.appendChild(scriptObj);
    } else {
      initLiveAgent('https://service.force.com');
    }
  };

  const updateAgentAvailabilityHandler = (event: UpdateAgentAvailabilityHandlerProps) => {
    setIsAgentAvailable(event?.detail);
  };

  useEffect(() => {
    document.addEventListener(UPDATE_AGENT_AVAIL_LISTENER, updateAgentAvailabilityHandler as EventListener);

    return () => {
      document.removeEventListener(UPDATE_AGENT_AVAIL_LISTENER, updateAgentAvailabilityHandler as EventListener);
    };
  }, []);

  return (
    <div data-testid="live-agent-script-wrapper">
      <ContactLiveAgentButton isAgentAvailable={isAgentAvailable} />
      <Script
        data-testid="live-agent-script"
        src="https://service.force.com/embeddedservice/5.0/esw.min.js"
        onLoad={onSalesforceServiceLoaded}
        onError={onLiveAgentError}
      />
    </div>
  );
};

export default LiveAgentWrapper;
